import React, {useEffect, useRef, useState} from "react";
import i18n from "../../i18n";

import {useCookieConsent} from "./provider";

function CookieCategory({
  id,
  title,
  description,
  status,
  checked,
  disabled,
  onChange,
}) {
  return (
    <div className={`cookie-consent__category${disabled ? " is-disabled" : ""}`}>
      <div className="cookie-consent__category-content">
        <div className="cookie-consent__category-header">
          <label
            className="cookie-consent__category-title"
            htmlFor={id}
          >
            {title}
          </label>

          <span className="cookie-consent__category-status">
            {status}
          </span>
        </div>

        <p className="cookie-consent__category-description">
          {description}
        </p>
      </div>

      <label className="cookie-consent__switch" htmlFor={id}>
        <input
          id={id}
          className="cookie-consent__switch-input"
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={onChange}
        />

        <span
          className="cookie-consent__switch-control"
          aria-hidden="true"
        />
      </label>
    </div>
  );
}

export default function CookieConsent() {
  const t = i18n.t.bind(i18n);

  const {
    consent,
    isBannerVisible,
    isSettingsOpen,
    acceptAll,
    rejectOptional,
    savePreferences,
    openSettings,
    closeSettings,
  } = useCookieConsent();

  const settingsTitleRef = useRef(null);
  const returnFocusRef = useRef(null);

  const [preferences, setPreferences] = useState({
    functional: false,
    analytics: false,
  });

  const isVisible = isBannerVisible || isSettingsOpen;

  useEffect(() => {
    if (!isSettingsOpen) {
      return;
    }

    setPreferences({
      functional: Boolean(consent && consent.functional),
      analytics: Boolean(consent && consent.analytics),
    });
  }, [isSettingsOpen, consent]);

  useEffect(() => {
    if (!isSettingsOpen || typeof document === "undefined") {
      return undefined;
    }

    returnFocusRef.current = document.activeElement;

    if (settingsTitleRef.current) {
      settingsTitleRef.current.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeSettings();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);

      const returnFocusElement = returnFocusRef.current;

      if (
        returnFocusElement &&
        document.body.contains(returnFocusElement)
      ) {
        window.requestAnimationFrame(() => {
          returnFocusElement.focus();
        });
      }
    };
  }, [isSettingsOpen, closeSettings]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    document.body.classList.toggle(
      "cookie-consent-visible",
      isVisible
    );

    return () => {
      document.body.classList.remove("cookie-consent-visible");
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  const handlePreferenceChange = (category) => (event) => {
    const {checked} = event.target;

    setPreferences((currentPreferences) => ({
      ...currentPreferences,
      [category]: checked,
    }));
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  if (isSettingsOpen) {
    return (
      <section
        className="cookie-consent cookie-consent--settings"
        role="dialog"
        aria-labelledby="cookie-consent-settings-title"
        aria-describedby="cookie-consent-settings-description"
      >
        <button
          className="cookie-consent__close"
          type="button"
          aria-label={t("cookieConsent.actions.close")}
          onClick={closeSettings}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M2 2L14 14M14 2L2 14"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>

        <div className="cookie-consent__heading">
          <span className="cookie-consent__eyebrow">
            {t("cookieConsent.settings.eyebrow")}
          </span>

          <h2
            id="cookie-consent-settings-title"
            ref={settingsTitleRef}
            className="cookie-consent__title"
            tabIndex="-1"
          >
            {t("cookieConsent.settings.title")}
          </h2>

          <p
            id="cookie-consent-settings-description"
            className="cookie-consent__description"
          >
            {t("cookieConsent.settings.description")}
          </p>
        </div>

        <div className="cookie-consent__categories">
          <CookieCategory
            id="cookie-consent-necessary"
            title={t("cookieConsent.categories.necessary.title")}
            description={t(
              "cookieConsent.categories.necessary.description"
            )}
            status={t("cookieConsent.categories.necessary.status")}
            checked
            disabled
          />

          <CookieCategory
            id="cookie-consent-functional"
            title={t("cookieConsent.categories.functional.title")}
            description={t(
              "cookieConsent.categories.functional.description"
            )}
            status={
              preferences.functional
                ? t("cookieConsent.categories.status.enabled")
                : t("cookieConsent.categories.status.disabled")
            }
            checked={preferences.functional}
            onChange={handlePreferenceChange("functional")}
          />

          <CookieCategory
            id="cookie-consent-analytics"
            title={t("cookieConsent.categories.analytics.title")}
            description={t(
              "cookieConsent.categories.analytics.description"
            )}
            status={
              preferences.analytics
                ? t("cookieConsent.categories.status.enabled")
                : t("cookieConsent.categories.status.disabled")
            }
            checked={preferences.analytics}
            onChange={handlePreferenceChange("analytics")}
          />
        </div>

        <p className="cookie-consent__policy">
          {t("cookieConsent.policy.text")}{" "}
          <a
            className="cookie-consent__policy-link"
            href="/privacy-page?tab=terms"
          >
            {t("cookieConsent.policy.link")}
          </a>
        </p>

        <div className="cookie-consent__actions cookie-consent__actions--settings">
          <button
            className="cookie-consent__button cookie-consent__button--primary"
            type="button"
            onClick={handleSavePreferences}
          >
            {t("cookieConsent.actions.save")}
          </button>

          <button
            className="cookie-consent__button cookie-consent__button--secondary"
            type="button"
            onClick={acceptAll}
          >
            {t("cookieConsent.actions.acceptAll")}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section
      className="cookie-consent"
      aria-labelledby="cookie-consent-title"
      aria-live="polite"
    >
      <div className="cookie-consent__heading">
        <span className="cookie-consent__eyebrow">
          {t("cookieConsent.banner.eyebrow")}
        </span>

        <h2
          id="cookie-consent-title"
          className="cookie-consent__title"
        >
          {t("cookieConsent.banner.title")}
        </h2>

        <p className="cookie-consent__description">
          {t("cookieConsent.banner.description")}
        </p>

        <p className="cookie-consent__policy">
          {t("cookieConsent.policy.text")}{" "}
          <a
            className="cookie-consent__policy-link"
            href="/privacy-page?tab=terms"
          >
            {t("cookieConsent.policy.link")}
          </a>
        </p>
      </div>

      <div className="cookie-consent__actions">
        <button
          className="cookie-consent__button cookie-consent__button--primary"
          type="button"
          onClick={acceptAll}
        >
          {t("cookieConsent.actions.acceptAll")}
        </button>

        <button
          className="cookie-consent__button cookie-consent__button--secondary"
          type="button"
          onClick={rejectOptional}
        >
          {t("cookieConsent.actions.rejectOptional")}
        </button>

        <button
          className="cookie-consent__button cookie-consent__button--tertiary"
          type="button"
          onClick={openSettings}
        >
          {t("cookieConsent.actions.settings")}
        </button>
      </div>
    </section>
  );
}